import { computed, ComputedRef, Ref, unref } from 'vue'
import { User } from '@/modules/users/interfaces/user'

export interface ContactItem {
	value: string
	formatted?: string
	type: 'primary' | 'notification'
	label: string
}

export interface ContactInfo {
	emails: ContactItem[]
	phones: ContactItem[]
}

/**
 * Formatea un número de teléfono con código de país
 * Ejemplo: 525512345678 -> +52 55 1234 5678
 */
function formatPhoneNumber(phone: string): string {
	// Remover cualquier caracter no numérico
	const cleaned = phone.replace(/\D/g, '')

	// Si ya tiene + al inicio, trabajar con eso
	const hasPlus = phone.startsWith('+')
	const number = hasPlus ? cleaned : cleaned

	// Detectar código de país común (México: 52)
	if (number.startsWith('52') && number.length >= 12) {
		// Formato para México: +52 55 1234 5678
		const countryCode = number.substring(0, 2)
		const areaCode = number.substring(2, 4)
		const firstPart = number.substring(4, 8)
		const secondPart = number.substring(8, 12)
		return `+${countryCode} ${areaCode} ${firstPart} ${secondPart}`
	}

	// Si tiene código de país pero no es México, formato genérico
	if (number.length > 10) {
		const countryCode = number.substring(0, number.length - 10)
		const restOfNumber = number.substring(number.length - 10)
		return `+${countryCode} ${restOfNumber.match(/.{1,4}/g)?.join(' ') || restOfNumber}`
	}

	// Formato local sin código de país
	return number.match(/.{1,4}/g)?.join(' ') || number
}

export function useUserContactInfo(
	user: Ref<User | undefined> | ComputedRef<User | undefined>
) {
	const contactInfo = computed<ContactInfo>(() => {
		if (!user.value) {
			return {
				emails: [],
				phones: [],
			}
		}

		const emails: ContactItem[] = []
		const phones: ContactItem[] = []

		// Agregar email principal si existe
		if (user.value.email) {
			emails.push({
				value: user.value.email,
				type: 'primary',
				label: 'Principal',
			})
		}

		// Agregar emails de notificaciones de servicios
		if (user.value.notificationSettings?.services?.email) {
			user.value.notificationSettings.services.email.forEach((email) => {
				// Evitar duplicados con el email principal
				if (email && email !== user.value?.email) {
					emails.push({
						value: email,
						type: 'notification',
						label: 'Notificaciones',
					})
				}
			})
		}

		// Agregar teléfonos de notificaciones de servicios (WhatsApp)
		if (user.value.notificationSettings?.services?.whatsapp) {
			user.value.notificationSettings.services.whatsapp.forEach((phone) => {
				if (phone) {
					phones.push({
						value: phone,
						formatted: formatPhoneNumber(phone),
						type: 'notification',
						label: 'Notificaciones',
					})
				}
			})
		}

		return {
			emails,
			phones,
		}
	})

	const hasEmails = computed(() => contactInfo.value.emails.length > 0)
	const hasPhones = computed(() => contactInfo.value.phones.length > 0)

	return {
		contactInfo,
		hasEmails,
		hasPhones,
	}
}
